/**
 * ScudoProfessionaleStep — v1.71.2 (feb-2026, semplificato).
 *
 * Sotto-wizard "Scudo Professionale — Operatore del Benessere" (3 sezioni):
 *   1. Massimale (readonly summary professioni + radio 500k/750k/1M)
 *   2. Sinistrosità ultimi 5 anni (SI/NO; se SI → <ClaimsInfoSheet> CIS Lloyd's)
 *   3. Dichiarazioni: 6 dich art. 1892 c.c. + Set Informativo IVASS Reg. 41/2018
 *
 * BRANDING: SOLO "Scudo Professionale — Operatore del Benessere" nel frontend.
 *
 * NB: `professioni_esercitate` NON è più chiesto qui — viene raccolto
 * nell'anagrafica attività principale (`AttivitaIn`) e mostrato in
 * sola lettura in Sez. 1.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import ClaimsInfoSheet, { emptyClaim } from "@/components/ClaimsInfoSheet";

// ────────────────────────────────────────────────────────────────────────────
// Costanti (mantenute allineate 1:1 con backend scudo_professionale_models.py)
// ────────────────────────────────────────────────────────────────────────────

// 25 professioni assicurabili — codice + label italiano.
// Esportato per essere riusato nello step attività (multi-select) e
// nel readonly summary della Sez. 1 del sotto-wizard.
export const SCUDO_PROFESSIONI = [
  { code: "agopuntore", label: "Agopuntore" },
  { code: "aromaterapeuta", label: "Aromaterapeuta" },
  { code: "assistente_sociale", label: "Assistente Sociale" },
  { code: "bionaturopata", label: "Bionaturopata" },
  { code: "chinesiterapeuta", label: "Chinesiterapeuta" },
  { code: "chiropratico", label: "Chiropratico" },
  { code: "career_coach", label: "Career Coach" },
  { code: "cosmetologo", label: "Cosmetologo" },
  { code: "elettro_agopuntore", label: "Elettro agopuntore" },
  { code: "erborista", label: "Erborista" },
  { code: "life_coach", label: "Life Coach" },
  { code: "maestro_yoga", label: "Maestro Yoga" },
  { code: "massaggiatore_aromatouch", label: "Massaggiatore Aromatouch" },
  { code: "musicoterapeuta", label: "Musicoterapeuta" },
  { code: "naturopata", label: "Naturopata" },
  { code: "nutripuntore", label: "Nutripuntore" },
  { code: "operatore_shiatzu", label: "Operatore Shiatzu" },
  { code: "pedagogo", label: "Pedagogo" },
  { code: "perfusionista", label: "Perfusionista" },
  { code: "pranoterapeuta", label: "Pranoterapeuta" },
  { code: "psicomotricista", label: "Psicomotricista" },
  { code: "riflessologo", label: "Riflessologo" },
  { code: "sessuologo", label: "Sessuologo" },
  { code: "tatuatore", label: "Tatuatore" },
  { code: "tecnico_ai_chi", label: "Tecnico Ai chi" },
];

const PROF_MAP = Object.fromEntries(SCUDO_PROFESSIONI.map((p) => [p.code, p.label]));

// Categorie danno specifiche Operatore del Benessere (12 voci, verbatim brief Christian).
// Passate a <ClaimsInfoSheet config.categorieDanno> per override della tassonomia sanitaria.
export const CATEGORIE_DANNO_BENESSERE = [
  { id: "reazione_allergica", label: "Reazione allergica / intolleranza a prodotti utilizzati" },
  { id: "infezione_post_trattamento", label: "Infezione post-trattamento (batterica, virale, fungina)" },
  { id: "lesione_cutanea", label: "Lesione cutanea (ustione, abrasione, taglio, granuloma)" },
  { id: "danno_estetico_permanente", label: "Danno estetico permanente (cicatrice, discromia, tatuaggio errato)" },
  { id: "reazione_integratori", label: "Reazione avversa a integratori / rimedi naturali / erbe" },
  { id: "caduta_cliente", label: "Caduta o infortunio del cliente durante il trattamento" },
  { id: "trasmissione_infezione_ematica", label: "Trasmissione infezione ematica (HIV, HBV, HCV)" },
  { id: "danno_psicologico", label: "Danno psicologico / dipendenza emotiva (coaching, sessuologia, pedagogia)" },
  { id: "violazione_privacy_gdpr", label: "Violazione riservatezza dati cliente (GDPR)" },
  { id: "danno_terzi_locali", label: "Danno a terzi in studio / locali (scivolate, urti)" },
  { id: "smaltimento_errato_materiali", label: "Danno da smaltimento errato materiali (aghi, prodotti chimici)" },
  { id: "altro", label: "Altro" },
];

export const SCUDO_DICHIARAZIONI_INTRO = "Il Sottoscritto/Proponente dichiara:";
export const SCUDO_DICHIARAZIONI = [
  { code: "dich_a", testo: "di prendere atto che questa Proposta non vincola nè gli Assicuratori nè il Proponente alla stipula del contratto di Assicurazione;" },
  { code: "dich_b", testo: "che le informazioni rese sul presente modulo sono veritiere e complete;" },
  { code: "dich_c", testo: "che nessuna compagnia assicurativa ha mai annullato o rifiutato di concedere o rinunciato a rinnovare una copertura assicurativa relativa alla sua R.C. Professionale;" },
  { code: "dich_d", testo: "di non aver sottaciuto alcun elemento rilevante per la valutazione del rischio;" },
  { code: "dich_e", testo: "di non aver mai subito procedimenti disciplinari a proprio carico, né di essere mai stato sospeso o radiato dal proprio ordine professionale;" },
  { code: "dich_f", testo: "che, se tra la data della compilazione del presente questionario e la data di emissione della polizza si verificassero variazioni rispetto alle informazioni fornite, accetta l'obbligo di notificare immediatamente le variazioni medesime agli assicuratori, i quali avranno il diritto di ritirare o modificare la loro quotazione o dare conferma di copertura." },
  { code: "set_informativo", testo: "di aver ricevuto e preso visione dei documenti componenti il SET INFORMATIVO come previsto dal Regolamento IVASS n° 41 del 2 agosto 2018." },
];

// ────────────────────────────────────────────────────────────────────────────
// State/helpers (exports)
// ────────────────────────────────────────────────────────────────────────────
export const initialScudoState = () => ({
  massimale: "",
  sinistri_5_anni: null, // null = non risposto, true/false = risposto
  sinistri_claims: [],
  dichiarazioni: SCUDO_DICHIARAZIONI.reduce((a, x) => ({ ...a, [x.code]: false }), {}),
});

export const isScudoValid = (sp) => {
  if (!sp) return false;
  if (!["500000", "750000", "1000000"].includes(sp.massimale)) return false;
  if (sp.sinistri_5_anni === null || sp.sinistri_5_anni === undefined) return false;
  if (sp.sinistri_5_anni === true) {
    if (!Array.isArray(sp.sinistri_claims) || sp.sinistri_claims.length === 0) return false;
  }
  if (!SCUDO_DICHIARAZIONI.every((d) => sp.dichiarazioni[d.code] === true)) return false;
  return true;
};

export const buildScudoPayload = (sp) => {
  if (!sp) return null;
  return {
    massimale: sp.massimale,
    sinistri_5_anni: !!sp.sinistri_5_anni,
    sinistri_claims: sp.sinistri_5_anni ? (sp.sinistri_claims || []) : null,
    dichiarazioni: sp.dichiarazioni,
  };
};

// ────────────────────────────────────────────────────────────────────────────
// UI helpers
// ────────────────────────────────────────────────────────────────────────────
function Section({ title, badge, defaultOpen, children, testId }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-[#E5E2DB] rounded-2xl overflow-hidden bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F5F4F0] transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <span className="flex items-center gap-3 text-left">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-white bg-[#163321] px-2 py-0.5 rounded-full">
            {badge}
          </span>
          <span className="font-heading text-sm sm:text-base text-[#0F1F14]">{title}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-[#4A5E51] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-5 pt-2">{children}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Componente principale
// ────────────────────────────────────────────────────────────────────────────
/**
 * Props:
 * - value: stato Scudo (vedi `initialScudoState`)
 * - onChange: (next) => void
 * - professioniSelezionate: string[] (codici professione dallo step Attività, readonly summary)
 * - onEditProfessioni: () => void (callback per tornare allo step Attività — es. setStep(0))
 */
export default function ScudoProfessionaleStep({ value, onChange, professioniSelezionate = [], onEditProfessioni }) {
  const sp = value;

  const setMassimale = (v) => onChange({ ...sp, massimale: v });
  const setSinistri = (v) => {
    // Se passa da SI a NO, resetta i claim; se passa a SI e non ci sono claim, ne aggiunge uno vuoto
    const next = { ...sp, sinistri_5_anni: v };
    if (v === true && (!sp.sinistri_claims || sp.sinistri_claims.length === 0)) {
      next.sinistri_claims = [emptyClaim()];
    }
    if (v === false) next.sinistri_claims = [];
    onChange(next);
  };
  const setClaims = (claims) => onChange({ ...sp, sinistri_claims: claims });
  const toggleDich = (code) => onChange({
    ...sp,
    dichiarazioni: { ...sp.dichiarazioni, [code]: !sp.dichiarazioni[code] },
  });

  return (
    <div>
      <h2 className="font-heading text-xl text-[#163321] mb-1">Scudo Professionale — Operatore del Benessere</h2>
      <p className="text-xs text-[#4A5E51] mb-4">
        Modulo di proposta RC Professionale dedicato alle professioni del benessere. Modulo conforme Reg. IVASS n. 41/2018.
      </p>

      <div className="space-y-3">
        {/* SEZ 1 — Massimale + readonly professioni */}
        <Section title="Massimale richiesto" badge="1 / 3" defaultOpen testId="scudo-sez1">
          {/* Readonly summary professioni */}
          <div className="mb-4 bg-[#F5F4F0] border border-[#E5E2DB] rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-wide text-[#4A5E51]">Professioni selezionate</span>
              {onEditProfessioni && (
                <button
                  type="button"
                  onClick={onEditProfessioni}
                  className="text-[11px] text-[#163321] underline underline-offset-2 hover:opacity-80"
                  data-testid="scudo-edit-professioni"
                >
                  Modifica nel passo precedente
                </button>
              )}
            </div>
            {professioniSelezionate.length === 0 ? (
              <p className="text-xs text-[#D97B58]">Nessuna professione selezionata. Torna al passo Attività per sceglierne almeno una.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {professioniSelezionate.map((code) => (
                  <span
                    key={code}
                    className="text-[12px] px-2 py-0.5 bg-white border border-[#163321]/20 text-[#163321] rounded-full"
                    data-testid={`scudo-prof-summary-${code}`}
                  >
                    {PROF_MAP[code] || code}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Radio massimale */}
          <label className="text-[11px] uppercase tracking-wide text-[#4A5E51] block mb-2">
            Massimale (per sinistro / aggregato annuo) *
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "500000", l: "€ 500.000" },
              { v: "750000", l: "€ 750.000" },
              { v: "1000000", l: "€ 1.000.000" },
            ].map((o) => (
              <label
                key={o.v}
                className={`text-sm text-center px-3 py-3 rounded-xl border cursor-pointer transition-all ${
                  sp.massimale === o.v ? "border-[#163321] bg-[#163321]/5 font-semibold" : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
                }`}
                data-testid={`scudo-mass-${o.v}`}
              >
                <input
                  type="radio"
                  name="scudo-massimale"
                  checked={sp.massimale === o.v}
                  onChange={() => setMassimale(o.v)}
                  className="sr-only"
                />
                {o.l}
              </label>
            ))}
          </div>
        </Section>

        {/* SEZ 2 — Sinistrosità */}
        <Section title="Sinistrosità ultimi 5 anni" badge="2 / 3" testId="scudo-sez2">
          <p className="text-sm text-[#0F1F14] mb-3">
            Sono state avanzate richieste di risarcimento o promosse azioni di responsabilità civile/penale/deontologica negli ultimi 5 anni? *
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { v: false, l: "No" },
              { v: true, l: "Sì" },
            ].map((o) => (
              <label
                key={String(o.v)}
                className={`text-sm text-center px-3 py-3 rounded-xl border cursor-pointer transition-all ${
                  sp.sinistri_5_anni === o.v ? "border-[#163321] bg-[#163321]/5 font-semibold" : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
                }`}
                data-testid={`scudo-sin-${o.v ? "si" : "no"}`}
              >
                <input
                  type="radio"
                  name="scudo-sinistri"
                  checked={sp.sinistri_5_anni === o.v}
                  onChange={() => setSinistri(o.v)}
                  className="sr-only"
                />
                {o.l}
              </label>
            ))}
          </div>

          {sp.sinistri_5_anni === true && (
            <div className="mt-4 pt-4 border-t border-[#E5E2DB]">
              <p className="text-xs text-[#4A5E51] mb-3">
                Compilare il <strong>Modulo CIS</strong> sottostante per ciascun sinistro o circostanza.
              </p>
              <ClaimsInfoSheet
                claims={sp.sinistri_claims || []}
                onClaimsChange={setClaims}
                config={{
                  professioni: SCUDO_PROFESSIONI.map((p) => p.label),
                  incarichi: [],
                  categorieDanno: CATEGORIE_DANNO_BENESSERE,
                }}
              />
            </div>
          )}
        </Section>

        {/* SEZ 3 — Dichiarazioni + Set Informativo */}
        <Section title="Dichiarazioni e Set Informativo" badge="3 / 3" testId="scudo-sez3">
          <p className="text-sm font-semibold text-[#0F1F14] mb-2">{SCUDO_DICHIARAZIONI_INTRO}</p>
          <div className="space-y-2">
            {SCUDO_DICHIARAZIONI.map((d) => (
              <label
                key={d.code}
                className="flex items-start gap-2 text-[12.5px] text-[#0F1F14]"
                data-testid={`scudo-dich-${d.code}`}
              >
                <input
                  type="checkbox"
                  checked={sp.dichiarazioni[d.code]}
                  onChange={() => toggleDich(d.code)}
                  className="mt-0.5 accent-[#163321]"
                />
                <span>{d.testo}</span>
              </label>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
